import { useMemo } from 'react'

import { SerializedError } from '@reduxjs/toolkit'
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query'

import { TrackedGame, TrackedGameBlueprint } from '../../../../features/live-events/types/TrackedGame'
import { BlueprintFamily, useGetTrackedBlueprintsQuery } from '../../../api/markets'
import { useBlueprintFamilyEventTypes } from './useBlueprintFamilyEventTypes'
import { useTrackedGamesWithAnalystMapping } from './useTrackedGamesAnalysts'

export type BlueprintFamilyGameUsage = {
  trackedGame: TrackedGame
  taggedBlueprints: TrackedGameBlueprint[]
}

export type BlueprintFamilyUsage = {
  blueprintFamily: BlueprintFamily
  games: BlueprintFamilyGameUsage[]
  gameBlueprintCount: number
  // Customer-facing event taxonomy (best-effort; both missing = uncategorized).
  eventTypeId?: string
  eventCategoryId?: string
}

export type GameBlueprintReference = {
  trackedGame: TrackedGame
  blueprint: TrackedGameBlueprint
}

export type BlueprintFamilyDataIssues = {
  untaggedGameBlueprints: GameBlueprintReference[]
  blueprintsTaggedToDiscontinued: (GameBlueprintReference & { blueprintFamily: BlueprintFamily })[]
  unusedBlueprintFamilies: BlueprintFamily[]
}

type BlueprintFamilyUsageResult = {
  usages: BlueprintFamilyUsage[]
  usageById: { [blueprintFamilyId: string]: BlueprintFamilyUsage }
  dataIssues: BlueprintFamilyDataIssues
  isLoading: boolean
  error?: FetchBaseQueryError | SerializedError
}

/**
 * Combines all Blueprint Families with the tracked games list into per-blueprint
 * usage data (which games and game-level blueprints are tagged to each Blueprint Family)
 * and derives data quality issues for sanity checking.
 *
 * There is no backend reverse lookup for games by Blueprint Family, so the
 * mapping is computed client-side from the embedded blueprints of each tracked game.
 */
export const useBlueprintFamilyUsage = (): BlueprintFamilyUsageResult => {
  const blueprintFamiliesQuery = useGetTrackedBlueprintsQuery()
  const trackedGamesQuery = useTrackedGamesWithAnalystMapping()
  const { eventTypeInfoByBlueprintId, isLoading: isEventTypesLoading } = useBlueprintFamilyEventTypes()

  const blueprintFamilies = blueprintFamiliesQuery.data
  const trackedGames = trackedGamesQuery.data

  return useMemo(() => {
    const gameUsagesByBlueprintFamilyId: { [blueprintFamilyId: string]: BlueprintFamilyGameUsage[] } = {}
    const untaggedGameBlueprints: GameBlueprintReference[] = []
    const blueprintFamilyById = (blueprintFamilies || []).reduce<{ [id: string]: BlueprintFamily }>((acc, blueprintFamily) => {
      acc[blueprintFamily.id] = blueprintFamily
      return acc
    }, {})

    const blueprintsTaggedToDiscontinued: BlueprintFamilyDataIssues['blueprintsTaggedToDiscontinued'] = []

    trackedGames?.forEach((trackedGame: TrackedGame) => {
      trackedGame.blueprints?.forEach((blueprint) => {
        if (!blueprint.blueprintFamilyId) {
          untaggedGameBlueprints.push({ trackedGame, blueprint })
          return
        }

        const usagesForBlueprint = gameUsagesByBlueprintFamilyId[blueprint.blueprintFamilyId] || []
        const existingGameUsage = usagesForBlueprint.find((usage) => usage.trackedGame.game.id === trackedGame.game.id)

        if (existingGameUsage) {
          existingGameUsage.taggedBlueprints.push(blueprint)
        } else {
          usagesForBlueprint.push({ trackedGame, taggedBlueprints: [blueprint] })
        }

        gameUsagesByBlueprintFamilyId[blueprint.blueprintFamilyId] = usagesForBlueprint

        const blueprintFamily = blueprintFamilyById[blueprint.blueprintFamilyId]
        if (blueprintFamily?.discontinued) {
          blueprintsTaggedToDiscontinued.push({ trackedGame, blueprint, blueprintFamily })
        }
      })
    })

    const usages: BlueprintFamilyUsage[] = (blueprintFamilies || []).map((blueprintFamily) => {
      const games = gameUsagesByBlueprintFamilyId[blueprintFamily.id] || []
      const eventTypeInfo = eventTypeInfoByBlueprintId[blueprintFamily.id]
      return {
        blueprintFamily,
        games,
        gameBlueprintCount: games.reduce((acc, gameUsage) => acc + gameUsage.taggedBlueprints.length, 0),
        eventTypeId: eventTypeInfo?.eventTypeId,
        eventCategoryId: eventTypeInfo?.eventCategoryId,
      }
    })

    const usageById = usages.reduce<{ [blueprintFamilyId: string]: BlueprintFamilyUsage }>((acc, usage) => {
      acc[usage.blueprintFamily.id] = usage
      return acc
    }, {})

    const dataIssues: BlueprintFamilyDataIssues = {
      untaggedGameBlueprints,
      blueprintsTaggedToDiscontinued,
      unusedBlueprintFamilies: usages.filter((usage) => usage.games.length === 0).map((usage) => usage.blueprintFamily),
    }

    return {
      usages,
      usageById,
      dataIssues,
      isLoading: blueprintFamiliesQuery.isLoading || trackedGamesQuery.isLoading || isEventTypesLoading,
      error: blueprintFamiliesQuery.error,
    }
  }, [
    blueprintFamilies,
    trackedGames,
    blueprintFamiliesQuery.isLoading,
    blueprintFamiliesQuery.error,
    trackedGamesQuery.isLoading,
    eventTypeInfoByBlueprintId,
    isEventTypesLoading,
  ])
}
